'use client';

import { useRef, useEffect, useState, ReactElement } from 'react';
import React from 'react';
import { Transition, TransitionStatus } from 'react-transition-group';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { ModalState } from '@nx-frontend/shared/types';
import { ModalWrapper } from './modal';
import { ModalContext, ModalContextValue } from './modal-context';
import { $ModalStore, closeModalEvent } from './modal-store';

const transitionClasses = {
  entering: 'opacity-1',
  entered: 'opacity-1',
  exiting: 'opacity-0',
  exited: 'opacity-0',
  unmounted: 'opacity-0',
};

export const ModalLayout = ({
  children,
  onClose,
  animate = true,
  payloadRequired = false,
  withoutWrapper = false,
  context,
}: {
  children?: ReactElement;
  onClose?: () => void;
  animate?: boolean;
  payloadRequired?: boolean;
  withoutWrapper?: boolean;
  context?: ModalContextValue;
}) => {
  const [store, closeModal] = useUnit([$ModalStore, closeModalEvent]);
  const [clientStore, setClientStore] = useState(store);
  const nodeRef = useRef(null);
  const duration = 200;

  useEffect(() => {
    setClientStore(store);
  }, [store]);

  if (payloadRequired && !(clientStore.length && clientStore.some((modal) => modal.payload))) {
    return null;
  }

  const getChildrenContent = (id: number) => {
    if (children) {
      return React.cloneElement(children, { modalId: id });
    }
  };

  const getModalContent = (state: TransitionStatus, modal: ModalState, index: number) => (
    <div
      key={index}
      data-state={state}
      className={classNames(
        'transition-opacity duration-200 ease-out inset-0 fixed',
        transitionClasses[state],
        modal.zIndex ? `z-[${modal.zIndex}]` : 'z-50'
      )}
    >
      <div
        ref={nodeRef}
        id={`ModalContainerLayout-${index}`}
        className={classNames(
          'fixed inset-0 bg-gray-400 opacity-40',
          modal.additionalLayoutStyles || '',
          modal.zIndex ? 'z-[' + modal.zIndex + ']' : 'z-40'
        )}
        onClick={onClose ? onClose : closeModal}
      ></div>
      {!withoutWrapper ? (
        <ModalWrapper
          id={modal.id}
          title={modal.title}
          templatePopup={modal.content}
          closePopUp={onClose ? onClose : closeModal}
          showCloseButton={modal.showCloseButton || false}
          closeButtonSize={modal.closeButtonSize || 24}
          isFullScreen={modal.isFullScreen}
          usePadding={modal.usePadding}
          transitionStatus={state}
        >
          {getChildrenContent(modal.id)}
        </ModalWrapper>
      ) : (
        getChildrenContent(modal.id)
      )}
    </div>
  );

  return (
    <ModalContext.Provider value={context ?? {}}>
      {clientStore.map((modal, index) => {
        return animate ? (
          <Transition in={modal.isShown} nodeRef={nodeRef} timeout={duration} unmountOnExit={true} key={index}>
            {(state) => getModalContent(state, modal, index)}
          </Transition>
        ) : (
          modal.isShown && getModalContent('entered', modal, index)
        );
      })}
    </ModalContext.Provider>
  );
};
